import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { Rng } from '../../engine/math/random';
import { box, cylinder, plane, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, colorize } from '../../engine/geo/mesh';
import type { Color } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Particles } from '../fx/Particles';
import { PALETTE, SPOTS, CISTERN_CORRIDORS, CISTERN_CEILING } from '../world/Layout';
import { worldHeight } from '../world/Terrain';
import { instanced, Instance } from '../world/Props';

export interface CisternHandles {
  update(playerPos: Vec3, particles: Particles, dt: number): void;
  ladderPos: Vec3;
}

/**
 * The Cistern (§2.5): stone galleries in a loop, ankle-deep water, pillars for lantern shadows,
 * a chasm across the top gallery, a flooded west arm, the ladder up, and the Black Keep's forecourt.
 */
export function buildCistern(ctx: Ctx): CisternHandles {
  const P = PALETTE.cistern;
  const rng = new Rng(505);
  const st = ctx.statics;
  const H = CISTERN_CEILING;
  const wallT = 3;
  const stone: Color = P[3];

  // walls: for each corridor rectangle, boxes on all four sides, then junction overlaps are simply extra stone.
  const parts: ReturnType<typeof box>[] = [];
  const isCorridor = (x: number, z: number) => CISTERN_CORRIDORS.some(([x0, z0, x1, z1]) => x > x0 && x < x1 && z > z0 && z < z1);
  const wall = (x0: number, z0: number, x1: number, z1: number) => {
    // skip wall segments that would sit inside another corridor (junctions)
    const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
    if (isCorridor(cx, cz)) return;
    parts.push(translate(box(x1 - x0, H + 1, z1 - z0, stone), cx, H / 2 - 0.5, cz));
    st.addBox(AABB.fromMinMax(x0, -2, z0, x1, H + 0.5, z1), 'cistern');
  };
  for (const [x0, z0, x1, z1] of CISTERN_CORRIDORS) {
    // split long walls into 4 m pieces so junction skipping works
    const segs = (ax: number, az: number, bx: number, bz: number, horizontal: boolean) => {
      const len = horizontal ? bx - ax : bz - az;
      const nseg = Math.ceil(len / 4);
      for (let k = 0; k < nseg; k++) {
        const t0 = k / nseg, t1 = (k + 1) / nseg;
        if (horizontal) wall(ax + len * t0, az, ax + len * t1, bz); else wall(ax, az + len * t0, bx, az + len * t1);
      }
    };
    segs(x0, z0 - wallT, x1, z0, true);      // north wall
    segs(x0, z1, x1, z1 + wallT, true);      // south wall
    segs(x0 - wallT, z0, x0, z1, false);     // west wall
    segs(x1, z0, x1 + wallT, z1, false);     // east wall
  }
  // ceiling slab over the whole gallery footprint
  parts.push(translate(box(120, 1, 130, [P[2][0], P[2][1], P[2][2]]), 4, H + 0.5, 611));
  st.addBox(AABB.fromMinMax(-56, H, 546, 64, H + 1, 676), 'ceiling');
  ctx.spawn('cistern.walls', new Transform(), combine(...parts));

  // pillars down the middle of the long corridors (shadow casters for the lantern)
  const pillars: Instance[] = [];
  for (const [x0, z0, x1, z1] of CISTERN_CORRIDORS) {
    const horizontal = x1 - x0 > z1 - z0;
    const len = horizontal ? x1 - x0 : z1 - z0;
    for (let d = 5; d < len - 4; d += 9) {
      const x = horizontal ? x0 + d : (x0 + x1) / 2 + (rng.chance(0.5) ? -2.2 : 2.2);
      const z = horizontal ? (z0 + z1) / 2 + (rng.chance(0.5) ? -2.2 : 2.2) : z0 + d;
      if (Math.abs(x - SPOTS.cisternChasm.x) < 4 && Math.abs(z - 560) < 5) continue;
      if (Math.hypot(x - SPOTS.cisternEntry.x, z - SPOTS.cisternEntry.z) < 3) continue;
      pillars.push({ pos: new Vec3(x, -0.3, z), yaw: rng.range(0, 6.3), scale: rng.jitter(0.06) });
      st.addCylinder(x, z, 0.55, -2, H, 'pillar');
    }
  }
  instanced(ctx, 'cistern.pillars', translate(cylinder(0.45, 0.55, H + 0.6, 8, stone), 0, (H + 0.6) / 2, 0), pillars);

  // water surface (ankle deep; black in the flooded arm)
  const w = plane(200, 200, 60, 60, [0.08, 0.1, 0.14]);
  translate(w, 0, 0, 600);
  colorize(w, (p) => (worldHeight(p.x, p.z) < -1.2 ? [0.0, 0.0, 0.005] : [0.08, 0.1, 0.14]));
  setEmissive(w, [0.02, 0.03, 0.05], 0.2);
  ctx.spawn('cistern.water', new Transform(), w, { transparent: true, alpha: 0.75, castShadow: false, wind: 0.15, noCull: true });

  // chasm rim: faint glowing moss so the pit reads from the entrance in the dark
  const cx = SPOTS.cisternChasm.x;
  const rim = combine(
    setEmissive(translate(box(0.3, 0.1, 8, [0.4, 0.9, 0.6]), cx - 2.0, -0.05, 560), [0.3, 0.9, 0.5], 0.9),
    setEmissive(translate(box(0.3, 0.1, 8, [0.4, 0.9, 0.6]), cx + 2.0, -0.05, 560), [0.3, 0.9, 0.5], 0.9),
  );
  ctx.spawn('cistern.rim', new Transform(), rim, { castShadow: false });

  // ladder up at the entry (a shaft in the ceiling with rungs)
  const e = SPOTS.cisternEntry;
  const ladder: ReturnType<typeof box>[] = [translate(box(0.12, H + 2, 0.12, [0.45, 0.35, 0.25]), e.x - 0.4, H / 2 + 0.5, e.z - 2.5), translate(box(0.12, H + 2, 0.12, [0.45, 0.35, 0.25]), e.x + 0.4, H / 2 + 0.5, e.z - 2.5)];
  for (let y = 0.4; y < H + 1.5; y += 0.5) ladder.push(translate(box(0.9, 0.08, 0.08, [0.45, 0.35, 0.25]), e.x, y, e.z - 2.5));
  const skylight = setEmissive(box(1.6, 0.2, 1.6, [0.5, 0.55, 0.7]), [0.4, 0.45, 0.6], 1.2);
  translate(skylight, e.x, H - 0.1, e.z - 2.5);
  ctx.spawn('cistern.ladder', new Transform(), combine(...ladder, skylight));

  // grey dot niche: a small alcove marker in the flooded arm's east wall
  const g = SPOTS.greyDotNiche;
  ctx.spawn('cistern.niche', new Transform(), translate(box(1.2, 1.2, 0.3, [P[1][0] * 1.4, P[1][1] * 1.4, P[1][2] * 1.4]), g.x + 3.9, 1.2, g.z));

  let acc = 0;
  return {
    ladderPos: new Vec3(e.x, 0, e.z - 2.5),
    update(playerPos: Vec3, particles: Particles, dt: number): void {
      acc += dt;
      if (acc < 0.5) return;
      acc = 0;
      // drips within earshot
      const x = playerPos.x + rng.range(-12, 12), z = playerPos.z + rng.range(-12, 12);
      if (!isCorridor(x, z)) return;
      const p = _p.set(x, H - 0.3, z);
      particles.mote(p, _v.set(0, -0.5, 0), 1.1, 0.05, [0.5, 0.6, 0.8], 0.3, 14);
      if (rng.chance(0.4)) ctx.sfx.drip(p);
    },
  };
}
const _p = new Vec3(), _v = new Vec3();
